import { Input } from '@/components/common/input'
import { SEARCH_FOR_KEY } from '@/constants/state'
import { useMixpanel } from '@/context/mixpanel'
import { useBorrowMarketsSearchState } from '@/hooks/useBorrowMarketsSearchState'
import { useDebounce } from '@/hooks/useDebounce'
import { useSearchTracking } from '@/hooks/useSearchTracking'
import { cn } from '@/utils/cn'
import { SECOND_IN_MILLISECONDS } from '@vaultsfyi/common/constants'
import type { ChangeEvent } from 'react'
import { useCallback, useEffect, useState } from 'react'
import { SearchIcon } from '../icons/search'

interface BorrowMarketsSearchInputProps {
  numberOfBorrowOptions: number
  trackingParams?: Record<string, string>
  className?: string
}

export const BorrowMarketsSearchInput = ({
  numberOfBorrowOptions,
  trackingParams,
  className,
}: BorrowMarketsSearchInputProps) => {
  const { searchFor, updateSearchState } = useBorrowMarketsSearchState()
  const [searchText, setSearchText] = useState(searchFor ?? '')
  const { trackEvent } = useMixpanel()
  const { onSearchTyped, onSearchKeyDown, onSearchBlur } = useSearchTracking({
    resultsCount: numberOfBorrowOptions,
    trackingParams,
  })

  useEffect(() => {
    setSearchText(searchFor ?? '')
  }, [searchFor])

  const onSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSearchText(event.target.value)
      updateSearchState([SEARCH_FOR_KEY, event.target.value])
      onSearchTyped(event.target.value)
    },
    [updateSearchState, onSearchTyped]
  )

  useDebounce(
    () => {
      if (searchText !== '') {
        trackEvent('Filter used', { Filter: 'Search', Value: searchText, ...trackingParams })
      }
    },
    [searchText],
    SECOND_IN_MILLISECONDS
  )

  return (
    <div className={cn('relative inline-flex items-center', className)}>
      <SearchIcon size={20} className="pointer-events-none absolute left-3 select-none text-body-foreground" />
      <Input
        placeholder={`Search through ${numberOfBorrowOptions} borrow options`}
        value={searchText}
        onChange={onSearchChange}
        onKeyDown={onSearchKeyDown}
        onBlur={onSearchBlur}
        inputMode="text"
        className="pl-10"
      />
    </div>
  )
}
